<i18n
  src="@/locales/views/organization/process/packing/show_process.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/harvested_batch.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/producer.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('list_processes')"
    :params="{ organization_id: organization_id }"
  />

  <bread-crumbs :labels="[module_name, harvest_process.name]" />

  <template v-if="load_data_general">
    <h1 class="page-title">
      <i class="fa-solid fa-crate-apple"></i>
      <span class="space-icon"></span>
      {{ $t("labels.title") }}
    </h1>

    <div class="mt-6 flex flex-row justify-between">
      <div class="panel-plain-label">
        <badge-default
          :label="
            harvested_batches.length +
            ' ' +
            $t('labels.total_number_harvested_batches')
          "
        />
      </div>
      <div class="space-x-3">
        <button
          v-if="harvested_batches.length > 0"
          type="button"
          @click.prevent="downloadRepHarvestXlsx()"
          class="btn-white"
        >
          <i
            :class="[
              'fa-solid',
              rep_harvest_downloading ? 'fa-sync fa-spin' : 'fa-file-excel'
            ]"
          >
          </i>
          <span class="space-icon"></span>
          {{ $t("labels.download_harvest_button") }}
        </button>
        <button
          v-if="harvested_batches.length > 0"
          type="button"
          @click.prevent="downloadRepPackingXlsx()"
          class="btn-white"
        >
          <i
            :class="[
              'fa-solid',
              rep_packing_downloading ? 'fa-sync fa-spin' : 'fa-file-excel'
            ]"
          >
          </i>
          <span class="space-icon"></span>
          {{ $t("labels.download_packing_button") }}
        </button>
        <button
          v-if="harvested_batches.length > 0"
          type="button"
          @click.prevent="downloadRepConsolidatedXlsx()"
          class="btn-white"
        >
          <i
            :class="[
              'fa-solid',
              rep_consolidated_downloading ? 'fa-sync fa-spin' : 'fa-file-excel'
            ]"
          >
          </i>
          <span class="space-icon"></span>
          {{ $t("labels.download_consolidated_button") }}
        </button>
        <new-button
          @click.prevent="goToNewHarvestedBatch()"
          :label="$t('labels.new_harvested_batch_button')"
        />
      </div>
    </div>
    <div class="mt-4 flex flex-col">
      <ag-grid-vue
        style="width: 100%; height: 650px"
        class="ag-theme-alpine w-full h-full"
        :columnDefs="columnDefs"
        @grid-ready="onGridReady"
        :defaultColDef="defaultColDef"
        :rowData="harvested_batches"
      ></ag-grid-vue>
    </div>
  </template>

  <loading-message v-else />
</template>

<script>
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridVue } from "ag-grid-vue3";
import GoToLink from "@/components/views/layout/ag_grid/GoToLink.vue";
import Requester from "@/views/organization/requesters/process/packing/show_process.js";
export default {
  name: "OrgProcessPackingShowProcess",
  components: { AgGridVue },
  data() {
    return {
      load_data_general: false,
      rep_harvest_downloading: false,
      rep_packing_downloading: false,
      rep_consolidated_downloading: false,
      harvest_process: {},
      harvested_batches: [],
      defaultColDef: {
        sortable: true,
        filter: true,
        floatingFilter: true
      },
      columnDefs: [
        {
          headerName: this.$t("model.harvested_batch.week"),
          field: "week",
          filter: "agNumberColumnFilter",
          width: 150
        },
        {
          headerName: this.$t("model.harvested_batch.registered_at"),
          field: "registered_at",
          filter: "agDateColumnFilter",
          cellRenderer: params => {
            return this.dateFormatter(params.value);
          }
        },
        {
          headerName: this.$t("model.harvested_batch.batch_code"),
          field: "batch_code",
          filter: true,
          cellRendererSelector: params => {
            return {
              component: GoToLink,
              params: {
                text: params.value,
                path_name: this.routeName("show_harvested_batch"),
                params_path: {
                  organization_id: this.organization_id,
                  harvested_batch_id: params.data.id
                }
              }
            };
          }
        },
        {
          headerName: this.$t("model.producer.producer_code"),
          field: "producer.producer_code",
          filter: true
        },
        {
          headerName: this.$t("model.harvested_batch.producer"),
          field: "producer.fullname",
          filter: true,
          width: 300
        },
        {
          headerName: this.$t("model.harvested_batch.crop_field"),
          field: "crop_field.name",
          filter: true
        },
        {
          headerName: this.$t("model.harvested_batch.total_weight"),
          field: "total_weight",
          width: 300,
          filter: "agNumberColumnFilter",
          cellRenderer: params => {
            return this.numberWithPrecision(params.value);
          }
        },
        {
          headerName: this.$t("model.harvested_batch.harvested_packages"),
          field: "harvested_packages_label",
          filter: true
        },
        {
          headerName: this.$t("model.harvested_batch.num_packages"),
          field: "num_packages",
          filter: true
        },
        {
          headerName: this.$t("model.harvested_batch.crop_variety"),
          field: "crop_variety.name",
          filter: true
        },
        {
          headerName: this.$t("model.harvested_batch.crop_type"),
          field: "crop_type.name",
          filter: true
        },
        {
          headerName: this.$t("model.harvested_batch.harvest_team"),
          field: "harvest_team_label",
          filter: true,
          cellRenderer: params => params.value.join(" - ")
        },
        {
          headerName: this.$t("model.harvested_batch.agro_exporter_process"),
          field: "agro_exporter_process_name",
          filter: true,
          width: 350,
          cellRenderer: params => params.value.join(" - ")
        }
      ]
    };
  },
  props: {
    organization_id: { required: true },
    harvest_process_id: { required: true }
  },
  methods: {
    loadData: async function () {
      await this.fetchHarvestProcess();
      await this.fetchHarvestedBatches();
      this.load_data_general = true;
    },
    fetchHarvestProcess: async function () {
      let response = await Requester.fetchHarvestProcess(
        this.organization_id,
        this.harvest_process_id
      );
      this.harvest_process = response.data;
    },
    fetchHarvestedBatches: async function () {
      let response = await Requester.fetchHarvestedBatches(
        this.organization_id,
        this.harvest_process_id
      );
      this.harvested_batches = response.data;
      this.harvested_batches.forEach(harvested_batch => {
        harvested_batch["harvested_packages_label"] =
          this.harvestedPackagesLabel(harvested_batch);
        harvested_batch["agro_exporter_process_name"] =
          this.agroExporterProcessName(harvested_batch);
        harvested_batch["harvest_team_label"] = this.harvestTeamLabel(
          harvested_batch.harvest_team
        );
      });
    },
    downloadRepHarvestXlsx: async function () {
      this.rep_harvest_downloading = true;
      try {
        var response = await Requester.downloadRepHarvestXlsx(
          this.organization_id,
          this.harvest_process_id
        );
        const file = response.data;
        var fileURL = window.URL.createObjectURL(new Blob([file]));
        var fileLink = document.createElement("a");

        fileLink.href = fileURL;
        fileLink.setAttribute(
          "download",
          `Reporte de cosecha - ${this.harvest_process.name}.xlsx`
        );
        document.body.appendChild(fileLink);

        fileLink.click();
      } catch (error) {
        error;
      }
      this.rep_harvest_downloading = false;
    },
    downloadRepPackingXlsx: async function () {
      this.rep_packing_downloading = true;
      try {
        var response = await Requester.downloadRepPackingXlsx(
          this.organization_id,
          this.harvest_process_id
        );
        const file = response.data;
        var fileURL = window.URL.createObjectURL(new Blob([file]));
        var fileLink = document.createElement("a");

        fileLink.href = fileURL;
        fileLink.setAttribute(
          "download",
          `Reporte de empaque - ${this.harvest_process.name}.xlsx`
        );
        document.body.appendChild(fileLink);
        fileLink.click();
      } catch (error) {
        error;
      }
      this.rep_packing_downloading = false;
    },
    downloadRepConsolidatedXlsx: async function () {
      this.rep_consolidated_downloading = true;
      try {
        var response = await Requester.downloadRepConsolidatedXlsx(
          this.organization_id,
          this.harvest_process_id
        );
        const file = response.data;
        var fileURL = window.URL.createObjectURL(new Blob([file]));
        var fileLink = document.createElement("a");

        fileLink.href = fileURL;
        fileLink.setAttribute(
          "download",
          `Reporte consolidado cosecha-empaque - ${this.harvest_process.name}.xlsx`
        );
        document.body.appendChild(fileLink);
        fileLink.click();
      } catch (error) {
        error;
      }
      this.rep_consolidated_downloading = false;
    },
    goToNewHarvestedBatch: function () {
      this.$router.push({
        name: this.routeName("new_harvested_batch"),
        params: {
          organization_id: this.organization_id,
          harvest_process_id: this.harvest_process_id
        }
      });
    },
    agroExporterProcessName: function (harvested_batch) {
      return [
        harvested_batch.agro_exporter_process?.name,
        harvested_batch.agro_exporter?.name
      ];
    },
    harvestedPackagesLabel: function (harvested_batch) {
      return (harvested_batch.harvested_packages || [])
        .map(
          harvested_package =>
            `${harvested_package.number} ${harvested_package.crop_har_package.name}`
        )
        .join(", ");
    },
    harvestTeamLabel: function (harvest_team) {
      return [harvest_team?.harvest_team_code, harvest_team?.foreman];
    },
    sizeToFit() {
      this.gridApi.sizeColumnsToFit({
        defaultMinWidth: 150
      });
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.sizeToFit();
    }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id
        }
      }).then(() => this.loadData())
    );
  }
};
</script>
